import { Navigate, useLocation } from 'react-router'
import { connect, ConnectedProps } from 'react-redux'
import AccessDenied from '../Pages/Error/AccessDenied'
import { PATH } from '../Constants/paths'
import { USER_ROLE } from '../Types/user'
import { isClientServiceSuspended } from '../utils/clientServiceStatus'
import { subscriptionInactivePath } from '../utils/billingReactivation'
import { saveRedirectUrl } from '../utils/redirectAfterLogin'
import { useClientCapabilities } from '../hooks/useClientCapabilities'
import { CAPABILITY_KEYS, CapabilityKey } from '../utils/capabilities'

interface OwnProps {
  component: React.ComponentType
  path?: string
  roles: Array<USER_ROLE>
  /** Soft-blocks the route (redirects to the first available section) when the client lacks this capability. */
  requiredCapability?: CapabilityKey
}

/** Checked in order — the first section the client has access to becomes the redirect target. */
const SECTION_FALLBACKS: Array<{ path: string; capability: CapabilityKey }> = [
  { path: PATH.Sales_Home, capability: CAPABILITY_KEYS.HOTLIST },
  { path: PATH.ALL_ACCOUNTS, capability: CAPABILITY_KEYS.ACCOUNTS },
  { path: PATH.INDUSTRY_INSIGHTS, capability: CAPABILITY_KEYS.INDUSTRIES },
  { path: PATH.MY_ASSETS, capability: CAPABILITY_KEYS.ASSETS },
]

const mapStateToProps = (state: AppState) => ({
  isAuthenticated: state.auth.isAuthenticated,
  loading: state.auth.loading,
  user: state.auth.user,
})

const connector = connect(mapStateToProps)

type Props = OwnProps & ConnectedProps<typeof connector>

export const PrivateRoute = connector(({ component: RouteComponent, roles, requiredCapability, isAuthenticated, loading, user }: Props) => {
  const location = useLocation()
  const userHasRequiredRole = user && roles.includes(user.role) ? true : false
  const { hasCapability, isAdminWithoutClient, loading: capabilitiesLoading } = useClientCapabilities()

  if (loading || (requiredCapability && capabilitiesLoading)) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
        aria-busy="true"
        aria-label="Loading session"
      >
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "var(--type-display-sm)", color: "var(--navy-blue-color)" }} aria-hidden />
      </div>
    )
  }

  if (!isAuthenticated || !user.access_token) {
    const intended = location.pathname + location.search + location.hash
    saveRedirectUrl(intended)
    return <Navigate to={PATH.LOGIN}/>
  }

    if (userHasRequiredRole) {
    if (isClientServiceSuspended(user)) {
      return <Navigate to={subscriptionInactivePath(location.search)} replace />
    }
    if (requiredCapability && !isAdminWithoutClient && !hasCapability(requiredCapability)) {
      const fallback = SECTION_FALLBACKS.find((section) => hasCapability(section.capability))
      return <Navigate to={fallback?.path ?? PATH.SETTINGS} replace />
    }
    return <RouteComponent />
  }

  return <AccessDenied />
})
